import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import {
    QrCode, Camera, Trash2, X, Edit3,
    Check, Link, Ticket, Sun, Share2, Zap, ExternalLink, DollarSign,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;

interface ParkTicket {
    id: string;
    payload: string;
    label: string;
    savedAt: string;
    expiresAt: string; // always savedAt + 48h
    facilityColor?: string;
}

type ModalState =
    | { type: 'none' }
    | { type: 'scan' }
    | { type: 'add'; initialPayload: string }
    | { type: 'detail'; ticket: ParkTicket }
    | { type: 'edit'; ticket: ParkTicket }
    | { type: 'qr'; ticket: ParkTicket }
    | { type: 'fee'; ticket: ParkTicket };

// ── Persistence ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'parkqr_tickets';
const BRIGHTNESS_DISMISSED_KEY = 'parkqr_brightness_hint_dismissed';

// ── T2 Systems MPOF integration ───────────────────────────────────────────────

const T2_API = 'https://auth-gateway.prod.link.t2systems.com/api/v1';

interface T2FacilityInfo {
    customerId: number;
    shortName: string;
    primaryColor: string;
    logoAttrId: string | null;
}

interface FeeData {
    price: number;
    transactionFee: number;
    facilityName: string | null;
    section: string | null;
    entryDateTime: string | null;
    ticketNumber: string | null;
    validations: unknown[];
}

function extractT2CustomerId(url: string): number | null {
    const m = url.match(/t2mpof\.com\/(\d+)/i);
    return m ? parseInt(m[1], 10) : null;
}

function shortFacilityName(name: string): string {
    const univMatch = name.match(/University of (\w+)\s*-\s*(\w+)/i);
    if (univMatch) return `U${univMatch[1][0].toUpperCase()}-${univMatch[2]}`;
    const dashParts = name.split(' - ');
    if (dashParts.length > 1) return dashParts[dashParts.length - 1].trim();
    return name.length > 22 ? `${name.substring(0, 20)}…` : name;
}

async function fetchT2Facility(customerId: number): Promise<T2FacilityInfo | null> {
    const cacheKey = `t2_facility_${customerId}`;
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) return JSON.parse(cached) as T2FacilityInfo;
    } catch { /* ignore */ }
    try {
        const res = await fetch(`${T2_API}/customers/${customerId}`, {
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const data = await res.json();
        const r = data.response;
        const attrs: Array<{ attributeLabel: string; attributeValue: string }> = r.attributes ?? [];
        const info: T2FacilityInfo = {
            customerId,
            shortName: shortFacilityName(r.customerName as string),
            primaryColor: attrs.find(a => a.attributeLabel === 'Primary Color')?.attributeValue ?? '#1976d2',
            logoAttrId: attrs.find(a => a.attributeLabel === 'Customer Logo')?.attributeValue ?? null,
        };
        try { localStorage.setItem(cacheKey, JSON.stringify(info)); } catch { /* quota */ }
        return info;
    } catch {
        return null;
    }
}

async function fetchT2LogoDataUrl(customerId: number, logoAttrId: string): Promise<string | null> {
    const cacheKey = `t2_logo_${customerId}`;
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) return cached;
    } catch { /* ignore */ }
    try {
        const res = await fetch(`${T2_API}/customers/${customerId}/images/${logoAttrId}`);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise<string | null>(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const url = reader.result as string;
                try { localStorage.setItem(cacheKey, url); } catch { /* quota */ }
                resolve(url);
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}


function loadTickets(): ParkTicket[] {
    try {
        const raw: Partial<ParkTicket>[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        const now = Date.now();
        const valid = raw
            .map(t => ({
                ...t,
                // backwards compat: old tickets without expiresAt get 48h from savedAt
                expiresAt: t.expiresAt ?? new Date(new Date(t.savedAt!).getTime() + FORTY_EIGHT_HOURS).toISOString(),
            } as ParkTicket))
            .filter(t => new Date(t.expiresAt).getTime() > now);
        // persist cleaned list if anything was removed
        if (valid.length !== raw.length) persistTickets(valid);
        return valid;
    } catch {
        return [];
    }
}

function persistTickets(tickets: ParkTicket[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tickets));
}

// Returns ms until expiry (negative if expired)
function msUntilExpiry(expiresAt: string): number {
    return new Date(expiresAt).getTime() - Date.now();
}


function expiryLabel(expiresAt: string): { text: string; urgent: boolean } {
    const ms = msUntilExpiry(expiresAt);
    if (ms <= 0) return { text: 'expired', urgent: true };
    const totalMinutes = Math.floor(ms / 60000);
    if (totalMinutes < 60) return { text: `expires in ${totalMinutes}m`, urgent: true };
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const urgent = hours < 2;
    if (mins === 0) return { text: `expires in ${hours}h`, urgent };
    return { text: `expires in ${hours}h ${mins}m`, urgent };
}

function formatDayTime(date: Date): string {
    const day = date.toLocaleDateString('en-US', { weekday: 'long' });
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day} ${time.toLowerCase()}`;
}

function formatDuration(entryIso: string): string {
    const totalMinutes = Math.floor((Date.now() - new Date(entryIso).getTime()) / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
}

function ticketSource(payload: string): string {
    try {
        const url = new URL(payload.replace(/^HTTPS?:\/\//i, 'https://'));
        const host = url.hostname.replace(/^www\./, '');
        const t2 = url.pathname.match(/^\/(\d+)/);
        return t2 ? `${host} · ${t2[1]}` : host;
    } catch {
        return payload.length > 42 ? `${payload.slice(0, 40)}…` : payload;
    }
}

// ── Animation variants ────────────────────────────────────────────────────────

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
};

// Bottom sheet slides up from bottom on mobile, slight spring feel
const sheetVariants = {
    hidden: { y: '100%' },
    visible: { y: 0, transition: { type: 'spring' as const, stiffness: 400, damping: 40 } },
    exit: { y: '100%', transition: { type: 'spring' as const, stiffness: 400, damping: 40 } },
};

// Grab handle shown at top of each bottom sheet
const GrabHandle: React.FC = () => (
    <div className="w-10 h-1 bg-black/10 rounded-full mx-auto mb-5" />
);

// ── Scan Modal ────────────────────────────────────────────────────────────────

interface ScanModalProps {
    onScanned: (url: string) => void;
    onManual: () => void;
    onCancel: () => void;
}

const ScanModal: React.FC<ScanModalProps> = ({ onScanned, onManual, onCancel }) => {
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [torchOn, setTorchOn] = useState(false);
    const [torchSupported, setTorchSupported] = useState(false);
    const hasScannedRef = useRef(false);

    const startLiveScan = async () => {
        setError(null);
        setIsScanning(true);
        hasScannedRef.current = false;

        await new Promise(resolve => setTimeout(resolve, 100));

        try {
            const scanner = new Html5Qrcode('qr-reader');
            scannerRef.current = scanner;

            await scanner.start(
                { facingMode: 'environment' },
                {
                    fps: 20,
                    experimentalFeatures: { useBarCodeDetectorIfSupported: true },
                } as never,
                (decodedText) => {
                    if (hasScannedRef.current) return;
                    hasScannedRef.current = true;
                    onScanned(decodedText);
                    setTimeout(() => {
                        scanner.stop().catch(() => {});
                        scannerRef.current = null;
                        setIsScanning(false);
                    }, 50);
                },
                () => {}
            );

            // Detect torch support after camera initialises
            const supported = !!(navigator.mediaDevices.getSupportedConstraints() as Record<string, boolean>)['torch'];
            setTorchSupported(supported);
        } catch {
            setError('Failed to start camera. Please allow camera permission.');
            setIsScanning(false);
        }
    };

    const stopScan = async () => {
        if (scannerRef.current) {
            try { await scannerRef.current.stop(); } catch { /* already stopped */ }
            scannerRef.current = null;
        }
        setTorchOn(false);
        setIsScanning(false);
    };

    const toggleTorch = async () => {
        if (!scannerRef.current) return;
        const next = !torchOn;
        try {
            await scannerRef.current.applyVideoConstraints({
                advanced: [{ torch: next } as MediaTrackConstraintSet],
            });
            setTorchOn(next);
        } catch { /* torch unavailable on this device */ }
    };

    useEffect(() => {
        startLiveScan();
        return () => { scannerRef.current?.stop().catch(() => {}); };
    }, []);

    return (
        <div className="fixed inset-0 bg-black z-[70] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-12 pb-4 shrink-0">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">ParkQR</p>
                    <h2 className="text-xl font-bold text-white tracking-tight">Scan Ticket QR</h2>
                </div>
                <button
                    onClick={() => { stopScan(); onCancel(); }}
                    className="p-3 rounded-full bg-white/10 active:bg-white/20"
                >
                    <X size={20} className="text-white" />
                </button>
            </div>

            {/* Camera fills available space */}
            <div className="flex-1 relative overflow-hidden">
                <div id="qr-reader" className="absolute inset-0" />

                {/* Corner-bracket viewfinder — visual guide without restricting scan area */}
                {isScanning && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="relative w-56 h-56">
                            <span className="absolute top-0 left-0 w-7 h-7 border-t-[3px] border-l-[3px] border-white rounded-tl-md" />
                            <span className="absolute top-0 right-0 w-7 h-7 border-t-[3px] border-r-[3px] border-white rounded-tr-md" />
                            <span className="absolute bottom-0 left-0 w-7 h-7 border-b-[3px] border-l-[3px] border-white rounded-bl-md" />
                            <span className="absolute bottom-0 right-0 w-7 h-7 border-b-[3px] border-r-[3px] border-white rounded-br-md" />
                        </div>
                    </div>
                )}

                {/* Scanning indicator + torch */}
                {isScanning && (
                    <div className="absolute bottom-6 inset-x-0 flex flex-col items-center gap-3">
                        <div className="flex items-center gap-3">
                            <div className="px-4 py-1.5 bg-black/70 rounded-full text-white text-xs font-mono flex items-center gap-2">
                                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                                Scanning…
                            </div>
                            {torchSupported && (
                                <button
                                    onClick={toggleTorch}
                                    className={`p-3 rounded-full transition-colors active:scale-95 ${
                                        torchOn ? 'bg-yellow-400 text-black' : 'bg-white/15 text-white'
                                    }`}
                                    aria-label="Toggle flashlight"
                                >
                                    <Zap size={18} />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={stopScan}
                            className="text-xs text-white/40 active:text-white underline"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {!isScanning && !error && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    </div>
                )}

                {error && (
                    <div className="absolute inset-0 flex items-center justify-center p-8">
                        <div className="flex flex-col items-center gap-4">
                            <div className="bg-red-900/40 border border-red-500/30 text-red-300 text-sm px-5 py-4 rounded-2xl text-center">
                                {error}
                            </div>
                            <button
                                onClick={startLiveScan}
                                className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-full text-sm font-medium active:scale-[0.985]"
                            >
                                <Camera size={14} />
                                <span>Try Again</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="shrink-0 px-6 pb-10 pt-4 text-center border-t border-white/10">
                <button
                    onClick={() => { stopScan(); onManual(); }}
                    className="flex items-center gap-2 mx-auto text-white/50 active:text-white/80 text-sm"
                >
                    <Link size={13} />
                    <span>Enter URL manually instead</span>
                </button>
            </div>
        </div>
    );
};

// ── Add Ticket Modal (bottom sheet) ──────────────────────────────────────────

interface AddTicketModalProps {
    initialPayload: string;
    onSave: (ticket: ParkTicket) => void;
    onCancel: () => void;
}

const AddTicketModal: React.FC<AddTicketModalProps> = ({ initialPayload, onSave, onCancel }) => {
    const [label, setLabel] = useState(() => formatDayTime(new Date()));
    const [floor, setFloor] = useState('');
    const [payload, setPayload] = useState(initialPayload);
    const [showPasteField, setShowPasteField] = useState(!initialPayload);
    const [facilityInfo, setFacilityInfo] = useState<T2FacilityInfo | null>(null);
    const floorRef = useRef<HTMLInputElement>(null);
    const labelRef = useRef<HTMLInputElement>(null);
    const labelEditedRef = useRef(false);

    useEffect(() => {
        setTimeout(() => floorRef.current?.focus(), 150);
    }, []);

    // Auto-detect T2 facility and pre-fill label when URL is a t2mpof.com receipt
    useEffect(() => {
        const customerId = extractT2CustomerId(payload);
        if (!customerId) { setFacilityInfo(null); return; }
        fetchT2Facility(customerId).then(info => {
            if (!info) return;
            setFacilityInfo(info);
            if (!labelEditedRef.current) {
                setLabel(`${info.shortName} · ${formatDayTime(new Date())}`);
            }
        });
    }, [payload]);

    const canSave = label.trim().length > 0 && payload.trim().length > 0;

    const handleSave = () => {
        if (!canSave) return;
        const savedAt = new Date().toISOString();
        const finalLabel = floor.trim() ? `${label.trim()} · ${floor.trim()}` : label.trim();
        onSave({
            id: crypto.randomUUID(),
            payload: payload.trim(),
            label: finalLabel,
            savedAt,
            expiresAt: new Date(Date.now() + FORTY_EIGHT_HOURS).toISOString(),
            ...(facilityInfo ? { facilityColor: facilityInfo.primaryColor } : {}),
        });
    };

    return (
        <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[70] flex items-end justify-center"
            onClick={onCancel}
        >
            <motion.div
                variants={sheetVariants}
                className="bg-white rounded-t-3xl border border-black/5 shadow-2xl p-6 w-full max-w-lg"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}
                onClick={e => e.stopPropagation()}
            >
                <GrabHandle />

                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-sm font-semibold tracking-wide text-gray-800">
                        {initialPayload ? 'Ticket Scanned' : 'Add Ticket'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="p-2.5 active:bg-gray-100 rounded-full transition-colors text-gray-400"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="space-y-4">
                    {initialPayload && !showPasteField ? (
                        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                            <div className="p-1 bg-green-500 rounded-full shrink-0 mt-0.5">
                                <Check size={10} strokeWidth={3} className="text-white" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-widest text-green-600 font-medium mb-0.5">
                                    Scanned
                                </p>
                                <p className="text-xs font-mono text-gray-500 break-all leading-relaxed">
                                    {initialPayload}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-medium block mb-1.5">
                                Ticket URL
                            </label>
                            <textarea
                                value={payload}
                                onChange={e => setPayload(e.target.value)}
                                placeholder="https://t2mpof.com/..."
                                rows={3}
                                className="w-full font-mono px-3 py-2.5 border border-black/10 rounded-xl focus:outline-none focus:border-black/25 placeholder-gray-300 bg-gray-50 resize-none"
                                style={{ fontSize: '16px' }}
                            />
                        </div>
                    )}

                    {initialPayload && (
                        <button
                            onClick={() => setShowPasteField(v => !v)}
                            className="flex items-center gap-1.5 text-[11px] text-gray-400 active:text-black transition-colors"
                        >
                            <Link size={11} />
                            <span>{showPasteField ? 'Hide paste field' : 'Or paste a different link'}</span>
                        </button>
                    )}

                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-gray-400 font-medium block mb-1.5">
                            Floor <span className="normal-case tracking-normal text-gray-300">(optional)</span>
                        </label>
                        <input
                            ref={floorRef}
                            type="text"
                            value={floor}
                            onChange={e => setFloor(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && labelRef.current?.focus()}
                            placeholder="e.g. L3, Level 2, Roof"
                            className="w-full px-4 py-3 border border-black/10 rounded-xl focus:outline-none focus:border-black/25 placeholder-gray-300 bg-gray-50"
                            style={{ fontSize: '16px' }}
                        />
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-gray-400 font-medium block mb-1.5">
                            Label
                        </label>
                        <input
                            ref={labelRef}
                            type="text"
                            value={label}
                            onChange={e => { labelEditedRef.current = true; setLabel(e.target.value); }}
                            onKeyDown={e => e.key === 'Enter' && handleSave()}
                            placeholder="e.g. Downtown Garage · May"
                            className="w-full px-4 py-3 border border-black/10 rounded-xl focus:outline-none focus:border-black/25 placeholder-gray-300 bg-gray-50"
                            style={{ fontSize: '16px' }}
                        />
                    </div>
                </div>

                <div className="flex gap-2 mt-6">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-3.5 border border-black/10 rounded-xl text-sm text-gray-500 active:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!canSave}
                        className="flex-1 py-3.5 bg-black text-white rounded-xl text-sm font-medium disabled:opacity-30 active:bg-gray-800"
                    >
                        Save Ticket
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

// ── Ticket Detail Modal (bottom sheet) ────────────────────────────────────────

interface TicketDetailModalProps {
    ticket: ParkTicket;
    onClose: () => void;
    onDisplayQR: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
    ticket, onClose, onDisplayQR, onEdit, onDelete,
}) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleShare = async () => {
        const deepLink = `https://cabhinav.com/parkqr?payload=${encodeURIComponent(ticket.payload)}&label=${encodeURIComponent(ticket.label)}`;
        const shareData = { title: ticket.label, url: deepLink };
        if (navigator.share) {
            try { await navigator.share(shareData); return; } catch { /* user cancelled */ }
        }
        // fallback: copy deep link to clipboard
        await navigator.clipboard.writeText(deepLink).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    return (
        <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-end justify-center"
            onClick={onClose}
        >
            <motion.div
                variants={sheetVariants}
                className="bg-white rounded-t-3xl border border-black/5 shadow-2xl p-6 w-full max-w-lg"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}
                onClick={e => e.stopPropagation()}
            >
                <GrabHandle />

                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-sm font-semibold tracking-wide text-gray-800 truncate pr-2">
                        {ticket.label}
                    </h2>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            onClick={handleShare}
                            className="p-2.5 active:bg-gray-100 rounded-full text-gray-400"
                            aria-label="Share ticket"
                        >
                            {copied ? <Check size={16} className="text-green-500" /> : <Share2 size={16} />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2.5 active:bg-gray-100 rounded-full text-gray-400"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>


                <div className="mb-6">
                    <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1">Saved</p>
                    <p className="text-sm text-gray-600">
                        {new Date(ticket.savedAt).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                        })}
                    </p>
                </div>

                <div className="space-y-2">
                    <button
                        onClick={onDisplayQR}
                        className="w-full py-4 bg-black text-white rounded-xl font-medium flex items-center justify-center gap-2 active:bg-gray-800"
                    >
                        <QrCode size={16} />
                        <span>Display QR Code</span>
                    </button>

                    <a
                        href={ticket.payload}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-3.5 border border-black/10 rounded-xl text-sm text-gray-700 flex items-center justify-center gap-2 active:bg-gray-50"
                    >
                        <ExternalLink size={14} />
                        <span>Open Link</span>
                    </a>

                    <div className="flex gap-2">
                        <button
                            onClick={onEdit}
                            className="flex-1 py-3.5 border border-black/10 rounded-xl text-sm text-gray-600 active:bg-gray-50 flex items-center justify-center gap-1.5"
                        >
                            <Edit3 size={13} />
                            <span>Rename</span>
                        </button>

                        {confirmDelete ? (
                            <div className="flex gap-1.5 flex-1">
                                <button
                                    onClick={onDelete}
                                    className="flex-1 py-3.5 bg-red-500 text-white rounded-xl text-sm font-medium active:bg-red-600"
                                >
                                    Delete
                                </button>
                                <button
                                    onClick={() => setConfirmDelete(false)}
                                    className="py-3.5 px-3 border border-black/10 rounded-xl text-gray-400 active:bg-gray-50"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setConfirmDelete(true)}
                                className="flex-1 py-3.5 border border-red-100 text-red-400 rounded-xl text-sm active:bg-red-50 flex items-center justify-center gap-1.5"
                            >
                                <Trash2 size={13} />
                                <span>Delete</span>
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

// ── Edit Label Modal (bottom sheet) ──────────────────────────────────────────

interface EditLabelModalProps {
    ticket: ParkTicket;
    onSave: (label: string) => void;
    onCancel: () => void;
}

const EditLabelModal: React.FC<EditLabelModalProps> = ({ ticket, onSave, onCancel }) => {
    const [label, setLabel] = useState(ticket.label);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTimeout(() => inputRef.current?.select(), 100);
    }, []);

    return (
        <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60] flex items-end justify-center"
            onClick={onCancel}
        >
            <motion.div
                variants={sheetVariants}
                className="bg-white rounded-t-3xl border border-black/5 shadow-2xl p-6 w-full max-w-lg"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}
                onClick={e => e.stopPropagation()}
            >
                <GrabHandle />

                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-sm font-semibold tracking-wide text-gray-800">Rename Ticket</h2>
                    <button
                        onClick={onCancel}
                        className="p-2.5 active:bg-gray-100 rounded-full text-gray-400"
                    >
                        <X size={16} />
                    </button>
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && label.trim() && onSave(label.trim())}
                    className="w-full px-4 py-3 border border-black/10 rounded-xl focus:outline-none focus:border-black/25 bg-gray-50"
                    style={{ fontSize: '16px' }}
                />

                <div className="flex gap-2 mt-4">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-3.5 border border-black/10 rounded-xl text-sm text-gray-500 active:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => label.trim() && onSave(label.trim())}
                        disabled={!label.trim()}
                        className="flex-1 py-3.5 bg-black text-white rounded-xl text-sm font-medium disabled:opacity-30 active:bg-gray-800"
                    >
                        Save
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

// ── QR Display Overlay ────────────────────────────────────────────────────────

interface QRDisplayOverlayProps {
    ticket: ParkTicket;
    onClose: () => void;
}

const QRDisplayOverlay: React.FC<QRDisplayOverlayProps> = ({ ticket, onClose }) => {
    const [qrSize, setQrSize] = useState(() =>
        Math.min(window.innerWidth, window.innerHeight) * 0.65
    );
    const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);
    const [showBrightnessHint, setShowBrightnessHint] = useState(() =>
        !localStorage.getItem(BRIGHTNESS_DISMISSED_KEY)
    );
    const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

    useEffect(() => {
        const update = () => {
            const landscape = window.innerWidth > window.innerHeight;
            setIsLandscape(landscape);
            setQrSize(Math.min(window.innerWidth, window.innerHeight) * (landscape ? 0.72 : 0.65));
        };
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null;
        if ('wakeLock' in navigator) {
            (navigator.wakeLock as WakeLock).request('screen')
                .then(wl => { wakeLock = wl; })
                .catch(() => {});
        }
        return () => { wakeLock?.release().catch(() => {}); };
    }, []);

    // Load facility logo when payload is a t2mpof.com URL
    useEffect(() => {
        const customerId = extractT2CustomerId(ticket.payload);
        if (!customerId) return;
        fetchT2Facility(customerId).then(info => {
            if (info?.logoAttrId) {
                fetchT2LogoDataUrl(customerId, info.logoAttrId).then(url => {
                    if (url) setLogoDataUrl(url);
                });
            }
        });
    }, [ticket.payload]);

    const dismissBrightnessHint = () => {
        localStorage.setItem(BRIGHTNESS_DISMISSED_KEY, '1');
        setShowBrightnessHint(false);
    };

    const brandColor = ticket.facilityColor ?? null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white z-[80] flex flex-col"
            onClick={onClose}
        >
            {/* Facility brand color bar */}
            {brandColor && (
                <div className="h-1 shrink-0" style={{ backgroundColor: brandColor }} />
            )}

            {/* Amber brightness hint bar — dismisses after first tap */}
            <AnimatePresence>
                {showBrightnessHint && (
                    <motion.button
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        onClick={e => { e.stopPropagation(); dismissBrightnessHint(); }}
                        className="flex items-center justify-center gap-2 w-full py-3.5 bg-amber-50 border-b border-amber-100 shrink-0"
                    >
                        <Sun size={15} className="text-amber-500 shrink-0" />
                        <span className="text-sm font-medium text-amber-700">
                            Turn up brightness for best results
                        </span>
                        <X size={13} className="text-amber-400 ml-2 shrink-0" />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Header */}
            <div
                className="flex items-center justify-between px-5 pt-10 pb-4 border-b border-black/5 shrink-0"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 min-w-0">
                    {logoDataUrl && (
                        <img
                            src={logoDataUrl}
                            alt=""
                            className="h-8 w-auto object-contain shrink-0"
                        />
                    )}
                    <div className="min-w-0">
                        <p
                            className="text-[10px] font-mono uppercase tracking-widest"
                            style={{ color: brandColor ?? '#9ca3af' }}
                        >
                            Ready to scan
                        </p>
                        <h2 className="text-base font-semibold text-gray-900 truncate max-w-[220px]">
                            {ticket.label}
                        </h2>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-mono uppercase tracking-wider active:opacity-80 shrink-0"
                    style={brandColor
                        ? { backgroundColor: brandColor, color: '#fff' }
                        : { backgroundColor: '#000', color: '#fff' }
                    }
                >
                    <Check size={11} strokeWidth={2.5} />
                    <span>Done</span>
                </button>
            </div>

            {/* QR code — portrait: centered column, landscape: side-by-side */}
            <div
                className={`flex-1 flex items-center justify-center p-6 ${
                    isLandscape ? 'flex-row gap-10' : 'flex-col'
                }`}
                onClick={e => e.stopPropagation()}
            >
                <div className="p-5 bg-white rounded-3xl shadow-2xl border border-black/5 shrink-0">
                    <QRCodeSVG
                        value={ticket.payload}
                        size={Math.round(qrSize)}
                        level="H"
                        bgColor="#ffffff"
                        fgColor="#000000"
                    />
                </div>

                {isLandscape && (
                    <p className="text-base font-semibold text-gray-900">{ticket.label}</p>
                )}
            </div>

        </motion.div>
    );
};

// ── Fee Modal ─────────────────────────────────────────────────────────────────

const feeCache = new Map<string, { data: FeeData; fetchedAt: number }>();
const FEE_CACHE_TTL = 90_000; // 90 seconds

const FeeModal: React.FC<{ ticket: ParkTicket; onClose: () => void }> = ({ ticket, onClose }) => {
    type Step = 'loading' | 'result' | 'error';
    const [step, setStep] = useState<Step>('loading');
    const [feeData, setFeeData] = useState<FeeData | null>(null);
    const customerId = extractT2CustomerId(ticket.payload);
    const token = ticket.payload.match(/t2mpof\.com\/\d+\/([^/?#\s]+)/i)?.[1];

    useEffect(() => {
        if (!customerId || !token) { setStep('error'); return; }
        const cacheKey = `${customerId}:${token}`;
        const cached = feeCache.get(cacheKey);
        if (cached && Date.now() - cached.fetchedAt < FEE_CACHE_TTL) {
            setFeeData(cached.data);
            setStep('result');
            return;
        }
        fetch('/api/t2-pricing', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customerId, barcodeData: token }),
        })
            .then(r => r.json())
            .then(data => {
                const r = data?.response;
                const cents = r?.amountDue;
                if (cents != null) {
                    const data: FeeData = {
                        price: Number(cents) / 100,
                        transactionFee: Number(r?.transactionFee ?? 0) / 100,
                        facilityName: r?.facilityName ?? null,
                        section: r?.transientParkingDescription ?? null,
                        entryDateTime: r?.entryDateTime ?? null,
                        ticketNumber: r?.ticketNumber ?? null,
                        validations: Array.isArray(r?.validations) ? r.validations : [],
                    };
                    feeCache.set(cacheKey, { data, fetchedAt: Date.now() });
                    setFeeData(data);
                    setStep('result');
                } else {
                    setStep('error');
                }
            })
            .catch(() => setStep('error'));
    }, []);

    return (
        <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[70] flex items-end justify-center"
            onClick={onClose}
        >
            <motion.div
                variants={sheetVariants}
                className="bg-white rounded-t-3xl border border-black/5 shadow-2xl w-full max-w-lg"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6">
                    <GrabHandle />
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-sm font-semibold tracking-wide text-gray-800">Parking Fee</h2>
                        <button onClick={onClose} className="p-2.5 active:bg-gray-100 rounded-full text-gray-400">
                            <X size={16} />
                        </button>
                    </div>

                    {step === 'loading' && (
                        <div className="py-10 flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-black/10 border-t-black rounded-full animate-spin" />
                            <p className="text-sm text-gray-400">Checking fee…</p>
                        </div>
                    )}

                    {step === 'result' && feeData !== null && (
                        <div className="space-y-4">
                            <div className="py-4 flex flex-col items-center gap-1">
                                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">Current fee</p>
                                <p className="text-5xl font-bold tracking-tight text-gray-900">${feeData.price.toFixed(2)}</p>
                                {feeData.transactionFee > 0 && (
                                    <p className="text-xs text-gray-400 mt-0.5">+${feeData.transactionFee.toFixed(2)} transaction fee</p>
                                )}
                            </div>

                            {(feeData.entryDateTime || feeData.section || feeData.facilityName) && (
                                <div className="bg-gray-50 rounded-2xl divide-y divide-black/5 overflow-hidden">
                                    {feeData.entryDateTime && (
                                        <div className="px-4 py-3 flex items-center justify-between gap-3">
                                            <span className="text-xs text-gray-600">
                                                {new Date(feeData.entryDateTime).toLocaleString('en-US', {
                                                    month: 'short', day: 'numeric',
                                                    hour: 'numeric', minute: '2-digit',
                                                })}
                                            </span>
                                            <span className="text-xs font-mono text-gray-400 shrink-0">
                                                {formatDuration(feeData.entryDateTime)} parked
                                            </span>
                                        </div>
                                    )}
                                    {feeData.section && (
                                        <div className="px-4 py-3">
                                            <span className="text-xs text-gray-600">{feeData.section}</span>
                                        </div>
                                    )}
                                    {feeData.facilityName && (
                                        <div className="px-4 py-3">
                                            <span className="text-xs font-mono text-gray-400">{feeData.facilityName}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {feeData.validations.length > 0 && (
                                <div className="px-4 py-2.5 bg-green-50 rounded-xl border border-green-100">
                                    <p className="text-xs text-green-700 font-medium">
                                        {feeData.validations.length} validation{feeData.validations.length !== 1 ? 's' : ''} applied
                                    </p>
                                </div>
                            )}

                            <a
                                href={ticket.payload.replace(/^HTTPS?:\/\//i, 'https://')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-4 bg-black text-white rounded-xl font-medium flex items-center justify-center gap-2 active:bg-gray-800"
                            >
                                <ExternalLink size={15} />
                                <span>Pay Now</span>
                            </a>
                            <button onClick={onClose} className="w-full py-3.5 border border-black/10 rounded-xl text-sm text-gray-500 active:bg-gray-50">
                                Dismiss
                            </button>
                        </div>
                    )}

                    {step === 'error' && (
                        <div className="py-8 text-center space-y-4">
                            <p className="text-sm text-gray-500">Couldn't fetch fee — ticket may be paid or expired.</p>
                            <button onClick={onClose} className="w-full py-3.5 border border-black/10 rounded-xl text-sm text-gray-500 active:bg-gray-50">
                                Dismiss
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

// ── Swipeable Ticket Row ──────────────────────────────────────────────────────

interface TicketRowProps {
    ticket: ParkTicket;
    index: number;
    onTap: () => void;
    onQRTap: () => void;
    onDelete: () => void;
    onFeeTap?: () => void;
}

const TicketRow: React.FC<TicketRowProps> = ({ ticket, index, onTap, onQRTap, onDelete, onFeeTap }) => {
    const isT2 = extractT2CustomerId(ticket.payload) !== null;
    const expiry = expiryLabel(ticket.expiresAt);
    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ x: '-110%', opacity: 0, transition: { duration: 0.28, ease: 'easeIn' } }}
            transition={{ delay: index * 0.04, duration: 0.25, ease: 'easeOut' }}
            className="relative overflow-hidden rounded-2xl"
        >
            {/* Red trash background revealed on swipe */}
            <div className="absolute inset-0 bg-red-500 rounded-2xl flex items-center justify-end pr-7">
                <Trash2 size={22} className="text-white" />
            </div>

            {/* Draggable row */}
            <motion.div
                drag="x"
                dragConstraints={{ left: -88, right: 0 }}
                dragElastic={{ left: 0.05, right: 0 }}
                onDragEnd={(_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
                    if (info.offset.x < -60) onDelete();
                }}
                className="relative z-10 bg-white border border-black/5 rounded-2xl px-5 py-5 active:bg-gray-50 cursor-pointer"
                onClick={onTap}
                whileTap={{ scale: 0.995 }}
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate" title={ticket.label}>
                            {ticket.label}
                        </p>
                        <p className="text-xs font-mono text-gray-400 mt-0.5 truncate" title={ticket.payload}>
                            {ticketSource(ticket.payload)}
                        </p>
                        <p className={`text-[10px] uppercase tracking-widest mt-1.5 font-medium ${expiry.urgent ? 'text-red-400' : 'text-gray-300'}`}>
                            {expiry.text}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {isT2 && onFeeTap && (
                            <button
                                onClick={e => { e.stopPropagation(); onFeeTap(); }}
                                className="h-10 px-3 border border-black/10 text-gray-500 rounded-xl text-xs font-mono uppercase tracking-wider active:bg-gray-50 flex items-center gap-1"
                            >
                                <DollarSign size={11} />
                                <span>Fee</span>
                            </button>
                        )}
                        <button
                            onClick={e => { e.stopPropagation(); onQRTap(); }}
                            className="w-12 h-12 bg-black text-white rounded-xl flex items-center justify-center active:bg-gray-700 transition-colors"
                            aria-label="Display QR code"
                        >
                            <QrCode size={20} />
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const MinimalParkQR: React.FC = () => {
    const [tickets, setTickets] = useState<ParkTicket[]>(() => loadTickets());
    const [modal, setModal] = useState<ModalState>({ type: 'none' });

    const openAddFlow = () => setModal({ type: 'scan' });

    // Auto-import ticket from shared deep link (?payload=...&label=...)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const payload = params.get('payload');
        const label = params.get('label');
        if (!payload || !label) return;

        // Clean URL immediately so refresh doesn't re-import
        window.history.replaceState({}, '', '/parkqr');

        const existing = loadTickets();
        // Skip if same payload already saved
        const ticket: ParkTicket = existing.find(t => t.payload === payload) ?? {
            id: crypto.randomUUID(),
            payload,
            label,
            savedAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + FORTY_EIGHT_HOURS).toISOString(),
        };

        if (!existing.some(t => t.payload === payload)) {
            const updated = [ticket, ...existing];
            persistTickets(updated);
            setTickets(updated);
        }

        // Go straight to QR display
        setModal({ type: 'qr', ticket });
    }, []);

    // Auto-open QR display when there's exactly one ticket (and no deep link)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payload')) return; // deep link takes priority
        const saved = loadTickets();
        if (saved.length === 1) setModal({ type: 'qr', ticket: saved[0] });
    }, []);

    const updateTickets = (updated: ParkTicket[]) => {
        setTickets(updated);
        persistTickets(updated);
    };

    // Auto-delete expired tickets while the page is open
    useEffect(() => {
        if (tickets.length === 0) return;
        const earliest = Math.min(...tickets.map(t => new Date(t.expiresAt).getTime()));
        const delay = earliest - Date.now();
        if (delay <= 0) {
            updateTickets(tickets.filter(t => msUntilExpiry(t.expiresAt) > 0));
            return;
        }
        const timer = setTimeout(() => {
            updateTickets(tickets.filter(t => msUntilExpiry(t.expiresAt) > 0));
        }, delay);
        return () => clearTimeout(timer);
    }, [tickets]);

    const handleSaveTicket = (ticket: ParkTicket) => {
        updateTickets([ticket, ...tickets]);
        setModal({ type: 'none' });
    };

    const handleDeleteTicket = (id: string) => {
        updateTickets(tickets.filter(t => t.id !== id));
        setModal({ type: 'none' });
    };

    const handleRenameTicket = (id: string, label: string) => {
        updateTickets(tickets.map(t => t.id === id ? { ...t, label } : t));
        setModal({ type: 'none' });
    };

    return (
        <div className="space-y-8">
            <Helmet>
                <title>ParkQR | Abhinav Chinnusamy</title>
                <meta name="description" content="Save and display parking ticket QR codes for gate scanning." />
                <link rel="icon" type="image/svg+xml" href="/icons/pwa-192.svg" />
            </Helmet>

            {/* Header */}
            <motion.header
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="border-b border-black/5 pb-6 flex items-center justify-between gap-4"
            >
                <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-black text-white rounded-lg shrink-0">
                        <QrCode size={16} strokeWidth={1.5} />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight leading-none">ParkQR</h1>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                            {tickets.length === 0
                                ? 'No tickets saved'
                                : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'} saved · All local`}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={openAddFlow}
                        className="flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-full text-xs font-mono uppercase tracking-wider active:bg-gray-800"
                    >
                        <Camera size={13} />
                        <span>Scan QR</span>
                    </button>
                </div>
            </motion.header>

            {/* Ticket list / empty state */}
            {tickets.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="py-16 flex flex-col items-center gap-4 text-center"
                >
                    <div className="p-4 bg-gray-50 rounded-2xl border border-black/5">
                        <Ticket size={28} strokeWidth={1.25} className="text-gray-300" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">No tickets saved yet</p>
                        <p className="text-xs text-gray-400 mt-0.5">Scan a ticket QR code to get started</p>
                    </div>
                    <button
                        onClick={openAddFlow}
                        className="mt-2 flex items-center gap-2 px-5 py-3 bg-black text-white rounded-full text-sm font-medium active:bg-gray-800"
                    >
                        <Camera size={14} />
                        <span>Scan QR Code</span>
                    </button>
                </motion.div>
            ) : (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.05 }}
                    className="space-y-2"
                >
                    <AnimatePresence mode="popLayout">
                        {tickets.map((ticket, i) => (
                            <TicketRow
                                key={ticket.id}
                                ticket={ticket}
                                index={i}
                                onTap={() => setModal({ type: 'detail', ticket })}
                                onQRTap={() => setModal({ type: 'qr', ticket })}
                                onDelete={() => handleDeleteTicket(ticket.id)}
                                onFeeTap={() => setModal({ type: 'fee', ticket })}
                            />
                        ))}
                    </AnimatePresence>
                    <p className="text-[10px] text-center text-gray-300 pt-1">
                        Swipe left to delete · Tap QR to display
                    </p>
                </motion.div>
            )}

            {/* Modals */}
            <AnimatePresence>
                {modal.type === 'scan' && (
                    <ScanModal
                        key="scan"
                        onScanned={url => setModal({ type: 'add', initialPayload: url })}
                        onManual={() => setModal({ type: 'add', initialPayload: '' })}
                        onCancel={() => setModal({ type: 'none' })}
                    />
                )}
                {modal.type === 'add' && (
                    <AddTicketModal
                        key="add"
                        initialPayload={modal.initialPayload}
                        onSave={handleSaveTicket}
                        onCancel={() => setModal({ type: 'none' })}
                    />
                )}
                {modal.type === 'detail' && (
                    <TicketDetailModal
                        key="detail"
                        ticket={modal.ticket}
                        onClose={() => setModal({ type: 'none' })}
                        onDisplayQR={() => setModal({ type: 'qr', ticket: modal.ticket })}
                        onEdit={() => setModal({ type: 'edit', ticket: modal.ticket })}
                        onDelete={() => handleDeleteTicket(modal.ticket.id)}
                    />
                )}
                {modal.type === 'edit' && (
                    <EditLabelModal
                        key="edit"
                        ticket={modal.ticket}
                        onSave={label => handleRenameTicket(modal.ticket.id, label)}
                        onCancel={() => setModal({ type: 'none' })}
                    />
                )}
                {modal.type === 'qr' && (
                    <QRDisplayOverlay
                        key="qr"
                        ticket={modal.ticket}
                        onClose={() => setModal({ type: 'none' })}
                    />
                )}
                {modal.type === 'fee' && (
                    <FeeModal
                        key="fee"
                        ticket={modal.ticket}
                        onClose={() => setModal({ type: 'none' })}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default MinimalParkQR;
